import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { teamKeys } from '@/hooks/query/keys';
import type { TeamDomain } from '@/types';

// `['teams', 'player-access']` shares the prefix: only lists of teams with their roster count.
function isTeamList(data: unknown): data is TeamDomain[] {
  return (
    Array.isArray(data) &&
    data.every(
      (team: unknown) =>
        typeof team === 'object' && team !== null && 'id' in team && 'roster' in team && Array.isArray(team.roster),
    )
  );
}

const isTeamListKey = (queryKey: QueryKey): boolean => queryKey[0] === teamKeys.all[0] && queryKey.length === 2;

/**
 * The season's team list already carries each roster (same backend select as
 * the detail): copy it into each team's own entry, so a team never opened
 * online still reads offline (plan de jeu, resume, another screen on
 * `useTeam`). No request added.
 *
 * Dated from when the list request left, not when it came back: a team saved
 * meanwhile keeps its newer detail. An invalidated detail is left to refetch.
 */
export function seedTeamDetailsFromLists(client: QueryClient, now: () => number = Date.now): () => void {
  const fetchStartedAt = new WeakMap<Query, number>();
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || !isTeamListKey(event.query.queryKey)) return;
    if (event.action.type === 'fetch') {
      fetchStartedAt.set(event.query, now());
      return;
    }
    if (event.action.type !== 'success') return;
    const { state } = event.query;
    if (!isTeamList(state.data)) return;
    // A list set by hand (no fetch) is as old as its own date.
    const updatedAt = Math.min(fetchStartedAt.get(event.query) ?? state.dataUpdatedAt, state.dataUpdatedAt);
    fetchStartedAt.delete(event.query);
    for (const team of state.data) {
      const key = teamKeys.detail(team.id);
      const detail = client.getQueryState(key);
      if (detail?.isInvalidated) continue;
      if (detail?.data !== undefined && detail.dataUpdatedAt >= updatedAt) continue;
      client.setQueryData<TeamDomain>(key, team, { updatedAt });
    }
  });
}
